/**
 * A class that acts like a Phaser.Group but is supposed to help manage tweens and animations
 * Instead of making a huge tween.onComplete.add(()=>{}) pyramid, HOPEFULLY(?) this manager thing
 * will help in making the tweens look better as a line by line thing instead of a gigantic pyramid
 * You can find an example of a gigantic tween pyramid example at Friendship_Chest.animateClaim();
 * You can find an example of a not-gigantic tween pyramid in GoalDropTutorial.animatePageTwo();
 *
 * I also took this idea from COOK's tutorial thing.
 * I also don't know if it's a good idea to do so or if this will cause memory problems for having so many promises or tween references
 * Either way I hope its a step in the right direction to avoiding gigantic tween pyramids!?
 *
 * @author Sandra Koo
 */
export default class TutorialGroup extends Phaser.Group {
  constructor(inGame, inParent) {
    super(inGame, inParent);

    this.signals = { // Signals for Window_popUpTutorial to deal with
      onContinueClick: new Phaser.Signal(),
    };

    this.everyTween = []; // Like \EVERY/ tween. Its possible to add tweens into this without adding it in action!
    this.everyTimer = [];

    this.actionTween = []; // Only for the current animation that needs to be stopped on a dime!
    this.actionTimer = [];
  }

  /**
   * A promised wrapper that holds a tween. When the tween finishes the promise resolves
   * Use this for your tweens. Add an `await` on the line that you call one of these to make the function stall
   * and wait for the tween to finish before moving on in the function (the function must also be an async func)
   * Not having an await will fire the tween and continue on (sort of like simultaneous things going off)
   * @param {Object} obj
   * @param {Object} tweenData
   * @param {number} tweenTime
   * @param {Phaser.Easing} easing
   * @param {number} repeat
   */
  wrapTween(obj, tweenData, tweenTime, easing, delay = 0, repeat = 0) {
    if (!this.shouldEventComplete()) { return null; }
    return new Promise((resolve) => {
      const tw = game.add.tween(obj)
        .to(tweenData, tweenTime, easing, true, delay, repeat);
      tw.onComplete.addOnce(() => {
        if (this.shouldEventComplete()) {
          resolve();
        }
      });
      this.everyTween.push(tw); // Pushes in every and action
      this.actionTween.push(tw);
    });
  }

  /**
   * Same thing as wrapTween but this one is a delay timer.
   * When the timer is complete, it'll resolve and continue.
   * Adding an `await` in front of one of these calls will stall the function until the timer resolves
   * Not having an await in front of it is... pretty pointless
   * @param {number} ms
   */
  wait(ms) {
    if (!this.shouldEventComplete()) { return null; }
    return new Promise((resolve) => {
      const ti = game.time.events.add(ms, () => {
        if (this.shouldEventComplete()) {
          resolve();
        }
      });
      this.everyTimer.push(ti);
      this.actionTimer.push(ti);
    });
  }

  /**
   * A function that gets called before a stalled tween/timer is made and just before it resolves.
   * Its supposed to check if the tween/timer should continue its functionality before/after it happens
   * Its supposed to be helpful, such that the tweens don't fire in case an object gets removed by Garbage Collection because the class
   * just got destroyed. Or/And then error because whatever it tried to tween/do is now undefined or something.
   * Feel free to replace this or add more to it
   */
  shouldEventComplete() {
    return this.game;
  }

  /**
   * Stops every action tween and timer.
   * Also removes them out from the everyTween/Timer array
   */
  stopAllActions() {
    if (this.actionTween) {
      for (let i = 0; i < this.actionTween.length; i++) {
        if (this.actionTween[i].stop) {
          this.actionTween[i].stop();
        }
      }
    }
    _.pull(this.everyTween, this.actionTween);
    this.actionTween = [];

    if (this.actionTimer) {
      for (let i = 0; i < this.actionTimer.length; i++) {
        if (this.actionTimer[i].destroy) {
          this.actionTimer[i].destroy();
        }
      }
    }
    _.pull(this.everyTimer, this.actionTimer);
    this.actionTimer = [];
  }

  /**
   * Stops every tween/timer and removes it
   */
  destroy() {
    if (this.everyTween) {
      for (let i = 0; i < this.everyTween.length; i++) {
        if (this.everyTween[i].stop) {
          this.everyTween[i].stop();
        }
      }
    }
    this.everyTween = null;

    if (this.everyTimer) {
      for (let i = 0; i < this.everyTimer.length; i++) {
        if (this.everyTimer[i].destroy) {
          this.everyTimer[i].destroy();
        }
      }
    }
    this.everyTimer = null;

    super.destroy();
  }
}
